import { getLotDetail } from '../../services/lotsService';
import { getSharedLotDetail } from '../../services/userLotsShareService';
import { buildLotSelectionState } from '../hooks/useLotSelection';
import {
  getDisplayUnits,
  groupUnitsByLot,
  normalizeLotsData,
} from './userLotUnits';
import { pickServiceDeficitFields } from './serviceDeficitDisplay';
import { mergeLotContextFields, pickLotContextFields } from './lotContextFields';

const hasMapCoords = (row) => {
  const lat = toNumber(row?.lat ?? row?.latitude);
  const lng = toNumber(row?.lng ?? row?.longitude ?? row?.lon);
  return lat != null && lng != null;
};

const toNumber = (v) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export const computeLotCenterFromBoundary = (boundary) => {
  if (!Array.isArray(boundary) || boundary.length === 0) return null;
  let sumLng = 0;
  let sumLat = 0;
  let count = 0;
  boundary.forEach((c) => {
    const lng = toNumber(c?.lng);
    const lat = toNumber(c?.lat);
    if (lng == null || lat == null) return;
    sumLng += lng;
    sumLat += lat;
    count += 1;
  });
  if (!count) return null;
  return { lng: sumLng / count, lat: sumLat / count };
};

const buildGeometryFromBoundary = (boundary) => {
  if (!Array.isArray(boundary) || boundary.length === 0) return null;
  let coordinates = boundary
    .map((coord) => {
      const lng = toNumber(coord?.lng);
      const lat = toNumber(coord?.lat);
      if (lng == null || lat == null) return null;
      return [lng, lat];
    })
    .filter(Boolean);
  if (
    coordinates.length > 0 &&
    (coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
      coordinates[0][1] !== coordinates[coordinates.length - 1][1])
  ) {
    coordinates = [...coordinates, [...coordinates[0]]];
  }
  if (coordinates.length > 2) {
    return { type: 'Polygon', coordinates: [coordinates] };
  }
  return null;
};

/** Normaliza fila de lista / API al shape que usa buildLotSelectionState (solo datos del lote, nunca de la unidad). */
/** @see docs/maps/lot-detail-field-parity.md */
export const normalizeLotRowForDock = (lot) => {
  if (!lot) return null;
  const lotId = lot.lot_id || lot.id;
  if (!lotId) return null;

  const baseStreet = lot.street_name || lot.street || lot.lot_street || '';
  const streetNumber =
    lot.street_number !== null &&
    lot.street_number !== undefined &&
    String(lot.street_number).trim() !== ''
      ? String(lot.street_number).trim()
      : '';

  const units = Array.isArray(lot.units) ? lot.units : [];
  const unitWithCoords = units.find((unit) => hasMapCoords(unit));

  return {
    id: lotId,
    name: lot.name || lot.lot_name || lotId,
    surface: lot.surface ?? lot.lot_surface ?? null,
    urbansurface: lot.urbansurface ?? lot.lot_urbansurface ?? null,
    built_footprint_m2: lot.built_footprint_m2 ?? lot.lot_built_footprint_m2 ?? null,
    max_footprint_m2: lot.max_footprint_m2 ?? lot.lot_max_footprint_m2 ?? null,
    max_buildable_m2: lot.max_buildable_m2 ?? lot.lot_max_buildable_m2 ?? null,
    state: lot.state ?? null,
    type: lot.type ?? lot.lot_type ?? null,
    valuation: lot.valuation ?? lot.lot_valuation ?? null,
    province: lot.province_name || lot.province,
    department: lot.department_name || lot.department,
    locality_name: lot.locality_name || lot.locality || lot.city || lot.lot_city || '',
    municipality_name: lot.municipality_name || lot.municipality || '',
    neighborhood: lot.neighborhood_name || lot.neighborhood || '',
    street_name: baseStreet,
    street_number: streetNumber,
    lat: lot.lat ?? unitWithCoords?.lat ?? null,
    lng: lot.lng ?? unitWithCoords?.lng ?? unitWithCoords?.lon ?? null,
    boundary: pickNonEmptyBoundary(lot.boundary, ...units.map((u) => u.boundary)),
    zoning_zone: lot.zoning_zone ?? lot.lot_zoning_zone ?? null,
    zoning_perfil: lot.zoning_perfil ?? lot.lot_zoning_perfil ?? null,
    flood_risk_pct: lot.flood_risk_pct ?? lot.lot_flood_risk_pct ?? null,
    chronic_poverty_pct: lot.chronic_poverty_pct ?? lot.lot_chronic_poverty_pct ?? null,
    population_density: lot.population_density ?? lot.lot_population_density ?? null,
    ...pickLotContextFields(lot),
    ...pickServiceDeficitFields(lot),
    companies: lot.companies || [],
  };
};

/** PolygonInfo para DockPanel al abrir un lote desde listas (misma forma que clic en el mapa). */
export const buildSharedListDockPolygonInfo = (lot) => {
  const lotData = normalizeLotRowForDock(lot);
  if (!lotData?.id) return null;

  const { polygon: _polygon, ...polygonInfo } = buildLotSelectionState({
    polygon: { id: String(lotData.id), layerId: `list-lot-${lotData.id}` },
    lotData,
  });

  return {
    ...polygonInfo,
    geometry: buildGeometryFromBoundary(polygonInfo.boundary),
    isLotPolygon: true,
  };
};

/** Ignora arrays vacíos (truthy en JS pero inválidos para planimetría). */
export const pickNonEmptyBoundary = (...candidates) => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 2) return candidate;
  }
  return null;
};

/**
 * Normaliza la fila seleccionada (panel o mapa) a la unidad plana con user_lot_id.
 * El panel pasa units[] con `id`; el mapa puede pasar solo lot_id de PMTiles.
 */
export const resolveSharedSelectionRow = (row, { flatUnits = [], groupedLots = [], lotIndex } = {}) => {
  if (!row) return null;

  const userLotId = row.user_lot_id ?? row.id;
  const lotId = row.lot_id;

  if (userLotId && lotIndex?.get) {
    const indexed = lotIndex.get(String(userLotId));
    if (indexed) return indexed;
  }

  if (userLotId) {
    const fromFlat = flatUnits.find((unit) => String(unit.user_lot_id) === String(userLotId));
    if (fromFlat) return fromFlat;
  }

  if (lotId) {
    const openLotOnly = row.item_type === 'lot' && !userLotId;
    const openUnitsList = row.item_type === 'units_list' && !userLotId;
    const preferDirectLot = openLotOnly || (!userLotId && !openUnitsList);
    if (preferDirectLot) {
      const directFromFlat = flatUnits.find((unit) => (
        String(unit.lot_id) === String(lotId)
        && (unit.item_type === 'lot' || !unit.user_lot_id)
      ));
      if (directFromFlat) return directFromFlat;
    }

    // units_list: no resolver a la primera UF del flat; el dock debe listar todas.
    if (!openLotOnly && !openUnitsList) {
      const fromFlatByLot = flatUnits.find((unit) => String(unit.lot_id) === String(lotId));
      if (fromFlatByLot) return fromFlatByLot;
    }
  }

  const group = groupedLots.find((lot) => String(lot.lot_id) === String(lotId));
  if (group) {
    const units = getDisplayUnits(group);
    if (!userLotId && (row.item_type === 'lot' || row.item_type === 'units_list' || units.length === 0)) {
      const resolvedType = row.item_type === 'units_list' ? 'units_list' : 'lot';
      return {
        ...group,
        ...row,
        item_type: resolvedType,
        user_lot_id: null,
        id: null,
        lot_id: group.lot_id,
        lot_name: group.lot_name ?? row.lot_name,
        province_name: group.province_name ?? row.province_name,
        department_name: group.department_name ?? row.department_name,
        locality_name: group.locality_name ?? row.locality_name ?? null,
        municipality_name: group.municipality_name ?? row.municipality_name ?? null,
        lat: group.lat ?? row.lat ?? null,
        lng: group.lng ?? row.lng ?? null,
        boundary: pickNonEmptyBoundary(group.boundary, row.boundary),
        lot_user_content: group.lot_user_content ?? row.lot_user_content ?? null,
      };
    }
    const unit = userLotId
      ? units.find((u) => String(u.id ?? u.user_lot_id) === String(userLotId))
      : units[0];
    if (unit) {
      return {
        ...unit,
        item_type: 'unit',
        user_lot_id: unit.id ?? unit.user_lot_id,
        lot_id: unit.lot_id ?? group.lot_id,
        lot_name: group.lot_name ?? unit.lot_name,
        province_name: group.province_name ?? unit.province_name,
        department_name: group.department_name ?? unit.department_name,
        locality_name: group.locality_name ?? unit.locality_name ?? null,
        municipality_name: group.municipality_name ?? unit.municipality_name ?? null,
        lat: unit.lat ?? group.lat ?? null,
        lng: unit.lng ?? group.lng ?? null,
        boundary: pickNonEmptyBoundary(unit.boundary, group.boundary),
        lot_user_content: group.lot_user_content ?? unit.lot_user_content ?? row.lot_user_content ?? null,
      };
    }
  }

  return {
    ...row,
    item_type: row.item_type || (userLotId ? 'unit' : 'lot'),
    user_lot_id: userLotId ?? null,
    lot_id: lotId ?? null,
  };
};

/** Campos de UF que getsharedlotdetail puede traer y deben preservarse en el dock. */
const UF_SHARE_DETAIL_KEYS = [
  'building_type',
  'rooms',
  'bedrooms',
  'full_bathrooms',
  'parking_lots',
  'total_surface',
  'covered_surface',
  'open_surface',
  'unit_floor',
  'year_built',
  'has_water',
  'has_gas',
  'has_electricity',
  'has_sewage',
  'attributes',
];

const pickUfShareFieldsFromDetail = (lot = {}, detail = {}) => {
  const patch = {};
  UF_SHARE_DETAIL_KEYS.forEach((key) => {
    if (key === 'attributes') {
      const fromDetail = detail.attributes && typeof detail.attributes === 'object'
        ? detail.attributes
        : null;
      const fromLot = lot.attributes && typeof lot.attributes === 'object'
        ? lot.attributes
        : null;
      if (fromDetail && Object.keys(fromDetail).length > 0) {
        patch.attributes = fromDetail;
      } else if (fromLot) {
        patch.attributes = fromLot;
      }
      return;
    }
    const fromDetail = detail[key];
    if (fromDetail !== null && fromDetail !== undefined && fromDetail !== '') {
      patch[key] = fromDetail;
      return;
    }
    if (lot[key] !== null && lot[key] !== undefined && lot[key] !== '') {
      patch[key] = lot[key];
    }
  });
  return patch;
};

/**
 * Clave estable de la selección en la lista compartida (lote + UF + tipo).
 * El detalle cargado se etiqueta con esta clave y solo se muestra si coincide
 * con la selección actual (evita mostrar datos del registro anterior).
 */
export const buildSharedSelectionKey = (row) => {
  if (!row?.lot_id) return null;
  const isLotLevel = row.item_type === 'lot' || row.item_type === 'units_list';
  const userLotId = isLotLevel ? '' : (row.user_lot_id ?? row.id ?? '');
  return `${row.lot_id}|${userLotId}|${row.item_type || ''}`;
};

/**
 * Detalle a mostrar para la selección actual. Si el detalle en estado es de
 * otra selección (respuesta/estado del registro anterior) → null + loading.
 */
export const resolveCurrentSharedDetail = ({
  selectedRow, detail, detailKey, detailLoading = false,
}) => {
  const selectionKey = buildSharedSelectionKey(selectedRow);
  const isCurrent = Boolean(selectionKey) && detailKey === selectionKey;
  return {
    selectionKey,
    detail: isCurrent ? detail : null,
    loading: Boolean(selectionKey) && (!isCurrent || Boolean(detailLoading)),
  };
};

/** Merge de `/getsharedlotdetail` (autorizado por token de lista compartida). */
export const mergeSharedListDetailFromApi = (lot, detail) => {
  if (!detail) return lot;

  const boundary = pickNonEmptyBoundary(detail.boundary, detail.lot_boundary, lot.boundary);
  const center = computeLotCenterFromBoundary(boundary);
  const catastralName = detail.lot_name || detail.name || null;

  return {
    ...lot,
    ...pickUfShareFieldsFromDetail(lot, detail),
    name: catastralName || lot.name,
    lot_name: catastralName || lot.lot_name,
    boundary,
    province_name: lot.province_name || detail.province_name || detail.province,
    department_name: lot.department_name || detail.department_name || detail.department,
    locality_name:
      lot.locality_name || lot.locality || detail.locality_name || detail.locality || detail.city,
    municipality_name: lot.municipality_name || detail.municipality_name || detail.municipality,
    neighborhood_name:
      lot.neighborhood_name || lot.neighborhood || detail.neighborhood_name || detail.neighborhood,
    street_name: lot.street_name || lot.street || detail.street_name || detail.street,
    street_number: lot.street_number ?? detail.street_number ?? null,
    urbansurface: lot.urbansurface ?? detail.lot_urbansurface ?? detail.urbansurface,
    surface: lot.surface ?? detail.lot_surface ?? detail.surface,
    built_footprint_m2: lot.built_footprint_m2 ?? detail.built_footprint_m2 ?? detail.lot_built_footprint_m2 ?? null,
    max_footprint_m2: lot.max_footprint_m2 ?? detail.max_footprint_m2 ?? detail.lot_max_footprint_m2 ?? null,
    max_buildable_m2: lot.max_buildable_m2 ?? detail.max_buildable_m2 ?? detail.lot_max_buildable_m2 ?? null,
    state: lot.state ?? detail.state ?? null,
    type: lot.type ?? detail.lot_type ?? detail.type ?? null,
    valuation: lot.valuation ?? detail.lot_valuation ?? detail.valuation,
    zoning_zone: detail.zoning_zone ?? detail.lot_zoning_zone ?? lot.zoning_zone ?? null,
    zoning_perfil: detail.zoning_perfil ?? detail.lot_zoning_perfil ?? lot.zoning_perfil ?? null,
    flood_risk_pct: detail.flood_risk_pct ?? detail.lot_flood_risk_pct ?? lot.flood_risk_pct ?? null,
    chronic_poverty_pct: detail.chronic_poverty_pct ?? detail.lot_chronic_poverty_pct ?? lot.chronic_poverty_pct ?? null,
    population_density: detail.population_density ?? detail.lot_population_density ?? lot.population_density ?? null,
    ...mergeLotContextFields(lot, detail),
    ...pickServiceDeficitFields(detail, lot),
    lot_user_content: detail.lot_user_content != null
      ? detail.lot_user_content
      : (lot.lot_user_content != null ? lot.lot_user_content : null),
    images: Array.isArray(detail.images)
      ? detail.images
      : (Array.isArray(lot.images) ? lot.images : []),
    cover_image: detail.cover_image ?? lot.cover_image ?? null,
    // Media de la UF: mismo dato que usa el listado de UF del dock (que guarda
    // el detalle crudo). Sin esto, abrir desde el mapa perdía videos/adjuntos.
    videos: Array.isArray(detail.videos)
      ? detail.videos
      : (Array.isArray(lot.videos) ? lot.videos : []),
    attachments: Array.isArray(detail.attachments)
      ? detail.attachments
      : (Array.isArray(lot.attachments) ? lot.attachments : []),
    lat:
      lot.lat ??
      detail.lat ??
      detail.latitude ??
      center?.lat ??
      null,
    lng:
      lot.lng ??
      detail.lng ??
      detail.longitude ??
      detail.lon ??
      center?.lng ??
      null,
  };
};

export const mergeLotDetailFromApi = (lot, lotData) => {
  if (!lotData) return lot;

  const center = computeLotCenterFromBoundary(lotData?.boundary || lot.boundary);
  const catastralName = lotData.name || lotData.lot_name || null;

  return {
    ...lot,
    name: catastralName || lot.name,
    lot_name: catastralName || lot.lot_name,
    boundary: pickNonEmptyBoundary(lotData.boundary, lot.boundary),
    province_name: lot.province_name || lotData.province_name || lotData.province,
    department_name: lot.department_name || lotData.department_name || lotData.department,
    locality_name:
      lot.locality_name || lot.locality || lotData.locality_name || lotData.locality || lotData.city,
    municipality_name: lot.municipality_name || lotData.municipality_name || lotData.municipality,
    neighborhood_name:
      lot.neighborhood_name || lot.neighborhood || lotData.neighborhood_name || lotData.neighborhood,
    street_name: lot.street_name || lot.street || lotData.street_name || lotData.street,
    street_number: lot.street_number ?? lotData.street_number ?? null,
    urbansurface: lot.urbansurface ?? lot.lot_urbansurface ?? lotData.urbansurface,
    surface: lot.surface ?? lot.lot_surface ?? lotData.surface,
    built_footprint_m2: lot.built_footprint_m2 ?? lot.lot_built_footprint_m2 ?? lotData.built_footprint_m2 ?? null,
    max_footprint_m2: lot.max_footprint_m2 ?? lot.lot_max_footprint_m2 ?? lotData.max_footprint_m2 ?? null,
    max_buildable_m2: lot.max_buildable_m2 ?? lot.lot_max_buildable_m2 ?? lotData.max_buildable_m2 ?? null,
    state: lot.state ?? lot.lot_state ?? lotData.state,
    type: lot.type ?? lot.lot_type ?? lotData.type,
    valuation: lot.valuation ?? lot.lot_valuation ?? lotData.valuation,
    zoning_zone: lotData.zoning_zone ?? lot.zoning_zone ?? null,
    zoning_perfil: lotData.zoning_perfil ?? lot.zoning_perfil ?? null,
    flood_risk_pct: lotData.flood_risk_pct ?? lot.flood_risk_pct ?? null,
    chronic_poverty_pct: lotData.chronic_poverty_pct ?? lot.chronic_poverty_pct ?? null,
    population_density: lotData.population_density ?? lot.population_density ?? null,
    ...mergeLotContextFields(lot, lotData),
    ...pickServiceDeficitFields(lotData, lot),
    lat: lot.lat ?? lotData.lat ?? center?.lat ?? null,
    lng: lot.lng ?? lotData.lng ?? center?.lng ?? null,
    companies: lot.companies || lotData.companies || [],
  };
};

export const fetchSharedUnitDetail = async (shareToken, userLotId) => {
  if (!shareToken || !userLotId) return null;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const res = await getSharedLotDetail(shareToken, { userLotId });
      if (res?._httpOk && res?.data) return res.data;
      if (res?._httpStatus === 429 && attempt === 0) {
        await new Promise((resolve) => setTimeout(resolve, 400));
        continue;
      }
      return null;
    } catch {
      if (attempt === 0) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        continue;
      }
      return null;
    }
  }
  return null;
};

/** Detalle de parcela directa del snapshot (sin user_lot). */
export const fetchSharedDirectLotDetail = async (shareToken, lotId) => {
  if (!shareToken || !lotId) return null;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const res = await getSharedLotDetail(shareToken, { lotId });
      if (res?._httpOk && res?.data) return res.data;
      if (res?._httpStatus === 429 && attempt === 0) {
        await new Promise((resolve) => setTimeout(resolve, 400));
        continue;
      }
      return null;
    } catch {
      if (attempt === 0) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        continue;
      }
      return null;
    }
  }
  return null;
};

export const getShareTokenFromRow = (row) =>
  row?.share_token || row?.shareToken || row?.token || '';

/** Un pin por lote catastral para capas de mapa y fitBounds. */
export const dedupeLotsForMapHighlight = (rows) => {
  const byLotId = new Map();
  (rows || []).forEach((row) => {
    const lotId = row?.lot_id;
    if (!lotId || byLotId.has(lotId)) return;
    byLotId.set(lotId, row);
  });
  return Array.from(byLotId.values());
};

/** Respuesta plana de getsharedlist (format=flat o default). */
export const normalizeSharedLotsFromApi = (data) => {
  const rows = Array.isArray(data?.lots) ? data.lots : [];
  return rows
    .map((row) => {
      const itemType = row.item_type || (row.id || row.user_lot_id ? 'unit' : 'lot');
      const userLotId = row.user_lot_id ?? (itemType === 'unit' ? row.id : null);
      return {
        ...row,
        item_type: itemType,
        user_lot_id: userLotId || null,
        lot_id: row.lot_id,
        lot_name: row.lot_name ?? null,
        lat: row.lat ?? null,
        lng: row.lng ?? null,
        locality_name: row.locality_name ?? null,
        municipality_name: row.municipality_name ?? null,
        unit_label: row.unit_label ?? null,
        unit_count: typeof row.unit_count === 'number'
          ? row.unit_count
          : (itemType === 'lot' ? 0 : 1),
        title: row.title || row.lot_name || row.lot_id || row.id,
        valuation: row.lot_valuation,
        urbansurface: row.lot_urbansurface,
        surface: row.lot_surface,
        built_footprint_m2: row.built_footprint_m2 ?? row.lot_built_footprint_m2 ?? null,
        max_footprint_m2: row.max_footprint_m2 ?? row.lot_max_footprint_m2 ?? null,
        max_buildable_m2: row.max_buildable_m2 ?? row.lot_max_buildable_m2 ?? null,
        state: row.lot_state,
        type: row.lot_type,
        zoning_zone: row.zoning_zone ?? row.lot_zoning_zone ?? null,
        zoning_perfil: row.zoning_perfil ?? row.lot_zoning_perfil ?? null,
        population_density: row.population_density ?? row.lot_population_density ?? null,
        ...pickLotContextFields(row),
        lot_user_content: row.lot_user_content != null ? row.lot_user_content : null,
      };
    })
    .filter((row) => row.lot_id && (row.item_type === 'lot' || row.user_lot_id));
};

export const isGroupedSharedListData = (data) => {
  if (data?.format === 'grouped') return true;
  const lots = data?.lots;
  return Array.isArray(lots) && lots.length > 0 && Array.isArray(lots[0]?.units);
};

/** Convierte lotes agrupados (getmylots / getsharedlist?format=grouped) a filas planas. */
export const flattenGroupedLotsToUnits = (groupedLots) => {
  const rows = [];
  (groupedLots || []).forEach((lot) => {
    const units = getDisplayUnits(lot);
    if (units.length === 0) {
      if (!lot?.lot_id) return;
      rows.push({
        ...lot,
        item_type: 'lot',
        user_lot_id: null,
        lot_id: lot.lot_id,
        lot_name: lot.lot_name ?? null,
        unit_count: 0,
        units: [],
        lot_user_content: lot.lot_user_content != null ? lot.lot_user_content : null,
      });
      return;
    }
    units.forEach((unit) => {
      rows.push({
        ...unit,
        item_type: 'unit',
        user_lot_id: unit.id ?? unit.user_lot_id,
        lot_id: unit.lot_id ?? lot.lot_id,
        lot_name: lot.lot_name ?? unit.lot_name ?? null,
        province_name: lot.province_name ?? unit.province_name,
        department_name: lot.department_name ?? unit.department_name,
        locality_name: lot.locality_name ?? unit.locality_name ?? null,
        municipality_name: lot.municipality_name ?? unit.municipality_name ?? null,
        lat: unit.lat ?? lot.lat ?? null,
        lng: unit.lng ?? lot.lng ?? null,
        boundary: unit.boundary ?? lot.boundary ?? null,
        lot_user_content: lot.lot_user_content != null
          ? lot.lot_user_content
          : (unit.lot_user_content != null ? unit.lot_user_content : null),
        filter_attributes: unit.filter_attributes ?? lot.filter_attributes ?? null,
        neighborhood_name: unit.neighborhood_name ?? lot.neighborhood_name ?? null,
        street_name: unit.street_name ?? lot.street_name ?? null,
      });
    });
  });
  return rows.filter((row) => row.lot_id && (row.item_type === 'lot' || row.user_lot_id));
};

/** true si alguna fila carece de lat/lng (backend legacy o lote sin geometría). */
export const needsSharedListMapEnrichment = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return false;
  return rows.some((row) => !hasMapCoords(row));
};

/**
 * Parsea data de getsharedlist y aplica enriquecimiento solo si faltan coords (fallback legacy).
 * @returns {{ flatUnits, groupedLots, total, totalUnits }}
 */
export const resolveSharedListLotsForMap = async (data, { shareToken } = {}) => {
  if (!data) {
    return { flatUnits: [], groupedLots: [], total: 0, totalUnits: 0 };
  }

  let flatUnits;
  let groupedLots;

  if (isGroupedSharedListData(data)) {
    groupedLots = normalizeLotsData(data.lots);
    flatUnits = flattenGroupedLotsToUnits(groupedLots);
  } else {
    flatUnits = normalizeSharedLotsFromApi(data);
    groupedLots = groupUnitsByLot(flatUnits);
  }

  if (needsSharedListMapEnrichment(flatUnits)) {
    flatUnits = await enrichSharedLotRowsForMap(flatUnits, { shareToken, onlyMissing: true });
    groupedLots = groupUnitsByLot(flatUnits);
  }

  return {
    flatUnits,
    groupedLots,
    total: data.total ?? groupedLots.length,
    totalUnits: data.total_units ?? flatUnits.length,
  };
};

/**
 * Enriquece filas de getsharedlist con boundary/lat/lng (fallback si el listado no trae coords).
 * Con shareToken usa getsharedlotdetail; complementa con getalllots solo para filas sin coords.
 */
export const enrichSharedLotRowsForMap = async (rows, options = {}) => {
  if (!Array.isArray(rows) || !rows.length) return [];
  if (!needsSharedListMapEnrichment(rows)) return rows;

  const shareToken = options?.shareToken || null;
  const onlyMissing = options?.onlyMissing !== false;
  let enriched = [...rows];

  const rowsMissingCoords = enriched.filter((row) => row?.user_lot_id && !hasMapCoords(row));

  if (shareToken && rowsMissingCoords.length > 0) {
    const uniqueUserLotIds = [
      ...new Set(rowsMissingCoords.map((row) => row.user_lot_id)),
    ];
    const detailByUserLotId = new Map();

    for (const userLotId of uniqueUserLotIds) {
      const detail = await fetchSharedUnitDetail(shareToken, userLotId);
      detailByUserLotId.set(userLotId, detail);
    }

    enriched = enriched.map((row) => {
      const detail = detailByUserLotId.get(row.user_lot_id);
      if (!detail) return row;
      if (onlyMissing && hasMapCoords(row)) return row;
      return mergeSharedListDetailFromApi(row, detail);
    });
  }

  const uniqueLotIds = [
    ...new Set(
      enriched
        .filter((row) => row?.lot_id && !hasMapCoords(row))
        .map((row) => row.lot_id)
    ),
  ];

  if (uniqueLotIds.length > 0) {
    const lotDataById = new Map();
    for (const lotId of uniqueLotIds) {
      const lotData = await getLotDetail(lotId);
      lotDataById.set(lotId, lotData);
    }

    enriched = enriched.map((row) => {
      if (onlyMissing && hasMapCoords(row)) return row;
      return mergeLotDetailFromApi(row, lotDataById.get(row.lot_id) || null);
    });
  }

  return enriched;
};
